import { useTranslation } from "react-i18next";
import { Grid, TextInput, Text, useMantineColorScheme } from "@mantine/core";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../../utils/atoms/projectDetailsAtom";
import PropTypes from "prop-types";
import { useMemo, useRef } from "react";
import { helpAtom } from "../../../../utils/atoms/helpAtom";
import { responsiveCol } from "../../../../constants/helper";

const OpexCapex = ({ form, handleInputChange, getFieldAttributes }) => {
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();

 const grayIconStyle = useMemo(
   () => ({
     borderRadius: "4px",
     padding: "4px",
   }),
   [colorScheme],
 ); 

  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const currency = projectDetails?.projectDTO?.currencyCode || "$";

  const fields = useMemo(
    () => [
      {
        id: "field-7",
        label: t("opexCapex.capitalExpenditure", "Capital Expenditure (One Time)"),
        placeholder: t(
          "opexCapex.enterCapitalExpenditure",
          "Enter Capital Expenditure"
        ),
        inputKey: "economicAttributes.gridParameters.newInstallationCost",
        onChangeKey: "newInstallationCost",
        rightText: `${currency}/kW`,
        rightMargin: "2.6rem",
      },
      {
        id: "field-8",
        label: t("opexCapex.operationMaintenance", "Operation and Maintenance Costs (Yearly)"),
        placeholder: t(
          "opexCapex.enterOperationMaintenance",
          "Enter Operation & Maintenance Cost"
        ),
        autoComplete: "none",
        name: "opex_capex.new_installation_cost",
        maxLength: 100,
        required: true,
        inputKey: "economicAttributes.gridParameters.omCost",
        onChangeKey: "omCost",
        rightText: `${currency}/kW/Year`,
        rightMargin: "3rem",
        rightSectionWidth: 60,
      },
    ],
    [currency, t]
  );

  const handleBlur = (event) => {
    if (!containerRef.current?.contains(event.relatedTarget)) {
      setHelperAtom({
        ...helperAtom,
        isFocused: false,
        helpSection: {
          title: "",
          description: "",
          type: "",
          other: "",
        },
      });
    }
  };

  const handleFocus = (event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  };
  return (
    <div ref={containerRef} onFocus={handleFocus} onBlur={handleBlur}>
      <Text component="span" fw={700}>
        {t("grid.opexCapex")}
      </Text>
      <Grid gutter="md" className="mt-2 mb-2">
        {fields.map(
          ({
            id,
            label,
            placeholder,
            autoComplete,
            name,
            maxLength,
            required,
            inputKey,
            onChangeKey,
            rightText,
            rightMargin,
            rightSectionWidth,
          }) => (
            <Grid.Col {...responsiveCol} key={id}>
              <TextInput
                {...getFieldAttributes(id)}
                data-id={id}
                label={label}
                type="text"
                placeholder={placeholder}
                autoComplete={autoComplete}
                name={name}
                maxLength={maxLength}
                required={required}
                {...form.getInputProps(inputKey)}
                onChange={(event) => {
                  handleInputChange(
                    "economicAttributes",
                    onChangeKey,
                    event.target.value
                  );
                }}
                rightSection={
                  <div style={{ ...grayIconStyle, marginRight: rightMargin }}>
                    {rightText}
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                rightSectionWidth={rightSectionWidth}
              />
            </Grid.Col>
          )
        )}
      </Grid>
    </div>
  );
};

OpexCapex.propTypes = {
  form: PropTypes.object,
  handleInputChange: PropTypes.func,
  getFieldAttributes: PropTypes.func,
};

export default OpexCapex;
